import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { selectThemes } from 'core/selectors/theme';
import { listRequest } from 'core/services/admin';
import { EntityTypes } from 'constants/entities';
import Listbox from 'app/componentsV2/Listbox/Listbox';
import { useSave } from 'core/hooks/useSave';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import classNames from 'classnames';
import { applyTheme } from 'utils/applyTheme';
import Button from 'app/componentsV2/Button/Button';
import { useNavigate } from 'react-router-dom';

interface ThemeSelectProps {
  courseNodeId: number;
  className?: string;
}

const ThemeSelect: React.FC<ThemeSelectProps> = ({ courseNodeId, className }) => {
  const dispatch = useAppDispatch();
  const themes = useSelector(selectThemes);
  const courseNode = useAppSelector(state => selectCourseNode(state, courseNodeId));
  const save = useSave(EntityTypes.COURSE_NODE);

  useEffect(() => {
    dispatch(listRequest({ type: EntityTypes.THEME, pagerInfo: { page: 1 } }));
  }, [dispatch]);

  const themeOptions = [
    { value: null, label: 'No theme' },
    ...themes.map(t => ({ value: t.id, label: (t as any).name || `Theme ${t.id}` })),
  ];

  const handleChange = useCallback(
    (val: number | null) => {
      save({ id: courseNodeId, themeId: val || null });
      const theme = themes.find(t => t.id === val);
      applyTheme(theme ? (theme as any).config : null);
    },
    [courseNodeId, save, themes],
  );

  const navigate = useNavigate();

  return (
    <div className={classNames(' w-64', className)}>
      <Listbox
        value={courseNode?.themeId ?? null}
        onChange={handleChange}
        options={themeOptions}
        placeholder="No theme"
      />

      <Button variant="none" className="underline" onClick={e => navigate('/theme')}>
        View themes
      </Button>
    </div>
  );
};

export default ThemeSelect;
